import React, { useMemo, useState } from 'react';
import { App, Button, Card, Form, Input, Modal, Popconfirm, Select, Space, Switch, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import inventoryService, { type ItemCategory } from '@/services/inventory.service';

const { Title, Text } = Typography;

const QUERY_KEY = ['inventory-categories'] as const;

export const ItemCategoryMasterPage: React.FC = () => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [editing, setEditing] = useState<ItemCategory | null>(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => inventoryService.getAllCategories(),
  });

  const parentNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of rows) {
      m.set(r.id, r.category_name);
    }
    return m;
  }, [rows]);

  const parentOptions = useMemo(() => {
    return rows
      .filter((r) => !editing || r.id !== editing.id)
      .map((r) => ({
        value: r.id,
        label: `${r.category_code} — ${r.category_name}`,
      }));
  }, [rows, editing]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ is_active: true });
    setOpen(true);
  };

  const openEdit = (row: ItemCategory) => {
    setEditing(row);
    form.setFieldsValue({
      category_code: row.category_code,
      category_name: row.category_name,
      description: row.description,
      parent_category_id: row.parent_category_id ?? undefined,
      is_active: row.is_active !== false,
    });
    setOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload = {
        category_code: String(values.category_code).trim(),
        category_name: String(values.category_name).trim(),
        description: values.description ? String(values.description).trim() : undefined,
        parent_category_id: values.parent_category_id || undefined,
        is_active: !!values.is_active,
      };
      if (editing) {
        await inventoryService.updateCategory(editing.id, payload);
        message.success('Item category updated');
      } else {
        await inventoryService.createCategory(payload);
        message.success('Item category created');
      }
      setOpen(false);
      setEditing(null);
      form.resetFields();
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    } catch (error: any) {
      if (error?.errorFields) return;
      message.error(error?.response?.data?.message || error?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await inventoryService.deleteCategory(id);
      message.success('Item category deleted');
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    } catch (error: any) {
      message.error(error?.response?.data?.message || error?.message || 'Delete failed');
    }
  };

  const columns: ColumnsType<ItemCategory> = [
    { title: 'Code', dataIndex: 'category_code', key: 'category_code', width: 120 },
    { title: 'Name', dataIndex: 'category_name', key: 'category_name', ellipsis: true },
    {
      title: 'Parent',
      key: 'parent',
      width: 200,
      ellipsis: true,
      render: (_: unknown, r: ItemCategory) =>
        r.parent_category_id ? parentNameById.get(r.parent_category_id) ?? '—' : '—',
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 90,
      render: (v: boolean) => (v !== false ? <Text type="success">Active</Text> : <Text type="secondary">Inactive</Text>),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_, row) => (
        <Space>
          <Button size="small" onClick={() => openEdit(row)}>
            Edit
          </Button>
          <Popconfirm
            title="Delete this category?"
            description="Only allowed if no items use it and it has no subcategories."
            onConfirm={() => handleDelete(row.id)}
            okText="Delete"
            okButtonProps={{ danger: true }}
          >
            <Button size="small" danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Space style={{ justifyContent: 'space-between', width: '100%' }}>
            <Title level={3} style={{ margin: 0 }}>
              Item categories
            </Title>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              New category
            </Button>
          </Space>
          <Text type="secondary">
            Used on the item master and imports (<Text code>category_code</Text>). Parent categories support a simple hierarchy.
          </Text>
          <Table<ItemCategory>
            rowKey="id"
            loading={isLoading}
            columns={columns}
            dataSource={rows}
            pagination={{ defaultPageSize: 20, showSizeChanger: true }}
          />
        </Space>
      </Card>

      <Modal
        title={editing ? 'Edit item category' : 'New item category'}
        open={open}
        onCancel={() => {
          setOpen(false);
          setEditing(null);
          form.resetFields();
        }}
        onOk={handleSave}
        confirmLoading={saving}
        destroyOnClose
        width={520}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 8 }}>
          <Form.Item
            name="category_code"
            label="Category code"
            rules={[{ required: true, message: 'Code is required' }, { max: 50, message: 'Max 50 characters' }]}
          >
            <Input placeholder="e.g. RAW-MTL" />
          </Form.Item>
          <Form.Item
            name="category_name"
            label="Category name"
            rules={[{ required: true, message: 'Name is required' }]}
          >
            <Input placeholder="Display name" />
          </Form.Item>
          <Form.Item name="parent_category_id" label="Parent category (optional)">
            <Select
              allowClear
              placeholder="None — top level"
              options={parentOptions}
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} placeholder="Optional" />
          </Form.Item>
          <Form.Item name="is_active" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
